import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Chip,
  Stack,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  CheckCircle as VerifiedIcon,
  Block as SuspendedIcon,
  Person as UserIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  Verified as VerifyIcon,
  Gavel as GavelIcon,
} from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import userService, { User } from '../services/api/userService';

interface UserWithDetails extends User {
  role: string;
  verification_status: string;
  is_verified: boolean;
  is_suspended: boolean;
  is_banned: boolean;
  created_at: string;
  rating?: number;
}

interface UserStats {
  total: number;
  active: number;
  suspended: number;
  verified: number;
  banned: number;
}

export default function UsersPage() {
  // State
  const [users, setUsers] = useState<UserWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<UserStats>({
    total: 0,
    active: 0,
    suspended: 0,
    verified: 0,
    banned: 0,
  });

  // Pagination
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalUsers, setTotalUsers] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithDetails | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Create user dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    user_type: 'client' as 'client' | 'artisan',
  });

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserWithDetails | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit form
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    is_active: true,
  });

  const [dialogLoading, setDialogLoading] = useState(false);
  const [userDetail, setUserDetail] = useState<any>(null);

  // Load users on filter/search/pagination change
  useEffect(() => {
    loadUsers();
  }, [paginationModel, searchQuery, roleFilter, verificationFilter, statusFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch users from API with search and role filter
      const response = await userService.getUsers(
        roleFilter || undefined,
        searchQuery || undefined
      );

      // Transform API response to UserWithDetails format
      const transformedUsers: UserWithDetails[] = (response.users || []).map((u: any) => ({
        id: u.id,
        name: u.name,
        phone: u.phone,
        email: u.email,
        user_type: u.user_type,
        status: u.status,
        verified: u.verified,
        created_at: u.created_at,
        stats: u.stats,
        role: u.user_type === 'artisan' ? 'artisan' : u.user_type === 'admin' || u.is_staff || u.is_superuser ? 'admin' : 'client',
        verification_status: u.verified ? 'verified' : 'pending',
        is_verified: u.verified,
        is_suspended: u.status === 'suspended',
        is_banned: u.status === 'banned',
        rating: u.stats?.rating || 0,
        artisan_profile: u.artisan_profile || null,
      }));

      // Apply additional filters on client side (verification and account status)
      let filtered = transformedUsers;

      if (verificationFilter) {
        if (verificationFilter === 'verified') {
          filtered = filtered.filter((u) => u.is_verified);
        } else if (verificationFilter === 'pending') {
          filtered = filtered.filter((u) => !u.is_verified && !u.is_suspended && !u.is_banned);
        } else if (verificationFilter === 'rejected') {
          filtered = filtered.filter((u) => !u.is_verified && u.is_suspended);
        }
      }

      if (statusFilter) {
        if (statusFilter === 'active') {
          filtered = filtered.filter((u) => u.status === 'active');
        } else if (statusFilter === 'suspended') {
          filtered = filtered.filter((u) => u.status === 'suspended');
        } else if (statusFilter === 'banned') {
          filtered = filtered.filter((u) => u.status === 'banned');
        }
      }

      // Calculate stats
      setStats({
        total: filtered.length,
        active: filtered.filter((u) => u.status === 'active').length,
        suspended: filtered.filter((u) => u.status === 'suspended').length,
        verified: filtered.filter((u) => u.is_verified && u.user_type === 'artisan').length,
        banned: filtered.filter((u) => u.status === 'banned').length,
      });

      // Apply pagination
      const start = paginationModel.page * paginationModel.pageSize;
      const end = start + paginationModel.pageSize;
      const paginatedUsers = filtered.slice(start, end);

      setUsers(paginatedUsers);
      setTotalUsers(filtered.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (user: UserWithDetails) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      is_active: !user.is_suspended,
    });
    setIsEditing(false);
    setUserDetail(null);
    setOpenDialog(true);
    // Fetch full detail (includes reports, wallet, moderation history)
    try {
      const detail = await userService.getUserDetail(user.id);
      setUserDetail(detail);
    } catch {
      // Non-blocking — dialog still works without detail
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setIsEditing(false);
    setUserDetail(null);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    try {
      setDialogLoading(true);

      // Update user via API
      await userService.updateUser(selectedUser.id, {
        first_name: editForm.name.split(' ')[0],
        last_name: editForm.name.split(' ')[1] || '',
        phone_number: editForm.phone,
      });

      setSelectedUser({
        ...selectedUser,
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        is_suspended: !editForm.is_active,
      });

      setIsEditing(false);
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save user');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSuspendUser = async (userId: string) => {
    try {
      setDialogLoading(true);
      await userService.suspendUser(userId, 'Admin action');
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to suspend user');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleOpenDeleteDialog = (user: UserWithDetails) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      setDeleteLoading(true);
      await userService.deleteUser(userToDelete.id);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
      setDeleteDialogOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!/^\d{4}$/.test(createForm.password)) {
      setError('Mobile app users need a 4-digit PIN. Enter exactly 4 digits.');
      return;
    }

    try {
      setCreateLoading(true);
      await userService.createUser(createForm);
      setCreateDialogOpen(false);
      setCreateForm({ first_name: '', last_name: '', email: '', phone_number: '', password: '', user_type: 'client' });
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleVerifyUser = async (userId: string) => {
    try {
      setDialogLoading(true);
      await userService.verifyUser(userId);
      await loadUsers();
      if (selectedUser) {
        setSelectedUser({ ...selectedUser, is_verified: true, verification_status: 'verified' });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to verify user');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleToggleSuspend = async (user: UserWithDetails) => {
    try {
      setDialogLoading(true);
      if (user.is_suspended) {
        await userService.unsuspendUser(user.id);
      } else {
        await userService.suspendUser(user.id, 'Suspended by admin');
      }
      await loadUsers();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user status');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleBanUser = async (user: UserWithDetails) => {
    try {
      setDialogLoading(true);
      await userService.banUser(user.id, 'Banned by admin');
      await loadUsers();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to ban user');
    } finally {
      setDialogLoading(false);
    }
  };

  // DataTable columns
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{params.value}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {params.row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
      flex: 0.8,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 100,
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.value === 'artisan' ? 'Artisan' : params.value === 'admin' ? 'Admin' : 'Client'}
          color={params.value === 'artisan' ? 'info' : params.value === 'admin' ? 'warning' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'artisan_services',
      headerName: 'Category / Services',
      width: 240,
      flex: 1.2,
      sortable: false,
      renderCell: (params) => {
        if (params.row.role !== 'artisan') {
          return <Typography variant="caption" color="text.secondary">N/A</Typography>;
        }
        const profile = params.row.artisan_profile;
        const category = profile?.service_category?.name || 'No category';
        const services = profile?.offered_services || [];

        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {category}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {services.length > 0
                ? services.map((service: any) => service.name).join(', ')
                : 'No services selected'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'verification_status',
      headerName: 'Verification',
      width: 120,
      flex: 0.8,
      renderCell: (params) => {
        if (params.row.role === 'admin') {
          return <Chip label="N/A" size="small" sx={{ color: 'text.disabled', borderColor: 'divider' }} variant="outlined" />;
        }
        return (
          <Chip
            icon={params.row.is_verified ? <VerifiedIcon /> : undefined}
            label={params.value === 'verified' ? 'Verified' : 'Pending'}
            color={params.value === 'verified' ? 'success' : 'warning'}
            size="small"
          />
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      flex: 0.6,
      renderCell: (params) => {
        if (params.row.is_banned) {
          return <Chip label="Banned" color="error" size="small" />;
        }
        if (params.row.is_suspended) {
          return <Chip label="Suspended" color="warning" size="small" />;
        }
        return <Chip label="Active" color="success" size="small" />;
      },
    },
    {
      field: 'created_at',
      headerName: 'Joined',
      width: 110,
      flex: 0.7,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString();
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            variant="text"
            startIcon={<EditIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(params.row as UserWithDetails);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDeleteDialog(params.row as UserWithDetails);
            }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  if (error && users.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Users Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage platform users, verify artisans, and handle account issues
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UserIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          + Create User
        </Button>
      </Box>

      {/* Error Alert */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Total Users"
            value={stats.total}
            subtitle="All platform users"
            icon={<PeopleIcon />}
            color="primary"
            trend={8.5}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Active Users"
            value={stats.active}
            subtitle={`${stats.total ? ((stats.active / stats.total) * 100).toFixed(0) : 0}% of total`}
            icon={<UserIcon />}
            color="success"
            trend={12}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Verified Artisans"
            value={stats.verified}
            subtitle="Verified professionals"
            icon={<VerifiedIcon />}
            color="success"
            trend={5.2}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Suspended Users"
            value={stats.suspended}
            subtitle="Accounts on hold"
            icon={<SuspendedIcon />}
            color="warning"
            trend={2}
            trendDirection="down"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Banned Users"
            value={stats.banned}
            subtitle="Permanently blocked"
            icon={<GavelIcon />}
            color="error"
            trend={0}
            trendDirection="down"
          />
        </Grid>
      </Grid>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Role"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="artisan">Artisan</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Verification"
                value={verificationFilter}
                onChange={(e) => setVerificationFilter(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Account Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="banned">Banned</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Table */}
      <DataTable
        title="Users List"
        columns={columns}
        rows={users}
        rowCount={totalUsers}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        loading={loading}
        error={error}
        onRowClick={(params) => handleOpenDialog(params.row as UserWithDetails)}
      />

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit User' : 'User Details'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!isEditing ? (
                // View Mode
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography>{selectedUser.name}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography>{selectedUser.email}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography>{selectedUser.phone || 'N/A'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Role
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedUser.role === 'artisan' ? 'Artisan' : selectedUser.role === 'admin' ? 'Admin' : 'Client'}
                        size="small"
                        color={selectedUser.role === 'artisan' ? 'info' : selectedUser.role === 'admin' ? 'warning' : 'default'}
                      />
                    </Box>
                  </Box>
                  {selectedUser.role !== 'admin' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Verification Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedUser.verification_status === 'verified' ? 'Verified' : 'Pending'}
                          size="small"
                          color={selectedUser.verification_status === 'verified' ? 'success' : 'warning'}
                        />
                      </Box>
                    </Box>
                  )}
                  {selectedUser.role === 'artisan' && (
                    <Box sx={{ mb: 2 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Artisan Services
                      </Typography>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Category
                        </Typography>
                        <Typography>
                          {selectedUser.artisan_profile?.service_category?.name || 'No category selected'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Services Offered
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                          {(selectedUser.artisan_profile?.offered_services || []).length > 0 ? (
                            selectedUser.artisan_profile?.offered_services.map((service: any) => (
                              <Chip key={service.id} label={service.name} size="small" variant="outlined" />
                            ))
                          ) : (
                            <Chip label="No services selected" size="small" color="warning" variant="outlined" />
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Account Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {selectedUser.is_banned ? (
                        <Chip label="Banned" size="small" color="error" />
                      ) : selectedUser.is_suspended ? (
                        <Chip label="Suspended" size="small" color="warning" />
                      ) : (
                        <Chip label="Active" size="small" color="success" />
                      )}
                    </Box>
                  </Box>

                  {/* ── User Reports ─────────────────────────────── */}
                  {userDetail && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          User Reports
                        </Typography>
                        {userDetail.report_count > 0 && (
                          <Chip
                            label={userDetail.report_count}
                            size="small"
                            color="error"
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        )}
                      </Box>
                      {userDetail.reports?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {userDetail.reports.map((r: any) => (
                            <Box
                              key={r.id}
                              sx={{
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: '#fff5f5',
                                border: '1px solid #fecaca',
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" fontWeight={600} color="error.main">
                                  {r.reason_display}
                                </Typography>
                                <Chip
                                  label={r.status}
                                  size="small"
                                  color={r.status === 'pending' ? 'warning' : r.status === 'actioned' ? 'error' : 'default'}
                                  sx={{ height: 18, fontSize: 10 }}
                                />
                              </Box>
                              {r.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  "{r.description}"
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                                Reported by {r.reported_by} · {new Date(r.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No reports filed against this user.</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                // Edit Mode
                <Box>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    sx={{ mb: 2 }}
                    disabled
                  />
                  <TextField
                    fullWidth
                    label="Phone"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editForm.is_active}
                        onChange={(e) =>
                          setEditForm({ ...editForm, is_active: e.target.checked })
                        }
                      />
                    }
                    label="Account Active"
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Close</Button>
          {!isEditing && selectedUser?.role !== 'admin' && (
            <>
              {!selectedUser?.is_verified && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<VerifyIcon />}
                  onClick={() => handleVerifyUser(selectedUser!.id)}
                  disabled={dialogLoading}
                >
                  Verify
                </Button>
              )}
              <Button
                variant="outlined"
                color={selectedUser?.is_suspended ? 'success' : 'warning'}
                startIcon={<BlockIcon />}
                onClick={() => handleToggleSuspend(selectedUser!)}
                disabled={dialogLoading}
              >
                {selectedUser?.is_suspended ? 'Unsuspend' : 'Suspend'}
              </Button>
              {!selectedUser?.is_banned && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  onClick={() => handleBanUser(selectedUser!)}
                  disabled={dialogLoading}
                >
                  Ban
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            </>
          )}
          {isEditing && (
            <>
              <Button onClick={() => setIsEditing(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSaveUser}
                disabled={dialogLoading}
              >
                {dialogLoading ? <CircularProgress size={24} /> : 'Save'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create New User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={createForm.first_name}
                onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={createForm.last_name}
                onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
              />
            </Box>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            />
            <TextField
              fullWidth
              label="Phone Number (e.g. +2348012345678)"
              value={createForm.phone_number}
              onChange={(e) => setCreateForm({ ...createForm, phone_number: e.target.value })}
            />
            <TextField
              fullWidth
              label="Mobile Login PIN"
              type="password"
              inputProps={{ inputMode: 'numeric', maxLength: 4, pattern: '[0-9]*' }}
              helperText="Client/artisan app login uses phone number + 4-digit PIN."
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            />
            <TextField
              fullWidth
              select
              label="User Type"
              value={createForm.user_type}
              onChange={(e) => setCreateForm({ ...createForm, user_type: e.target.value as 'client' | 'artisan' })}
            >
              <MenuItem value="client">Client</MenuItem>
              <MenuItem value="artisan">Artisan</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={createLoading || !createForm.first_name || !createForm.email || !createForm.phone_number || !/^\d{4}$/.test(createForm.password)}
            startIcon={createLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {createLoading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete{' '}
            <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone. All user data will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
